import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { get } from 'es-toolkit/compat'
import env from '~/libs/env'
import { useBorrowerStore } from '~/store/borrower'
import { useDealsStore } from '~/store/deals'
import { useApprovalsStore } from '~/store/approvals'
import { getApprovalOption, getApprovalMaxAmount, convertTermToMonths } from '~/libs/approval-helper'
import OfferTotal, { getMonthlyPaymentFromPeriod } from '~/libs/offer-total'
import { OPTION_INSIGHTS } from '~/libs/portal-constants'

// On timeout, options render w/out tags instead of the page waiting indefinitely.
export const HALO_TAGS_TIMEOUT_MS = 10000

export const useHaloTagsStore = defineStore('haloTags', () => {
  const { $axios } = useNuxtApp()

  const borrowerStore = useBorrowerStore()
  const dealsStore = useDealsStore()
  const approvalsStore = useApprovalsStore()

/*
  ███████ ████████  █████  ████████ ███████
  ██         ██    ██   ██    ██    ██
  ███████    ██    ███████    ██    █████
       ██    ██    ██   ██    ██    ██
  ███████    ██    ██   ██    ██    ███████
  STATE
*/
  // Halo tags keyed by option_id for easy lookup from option cards.
  const haloTagsByOptionId = ref({})
  const loaded = ref(false)
  // True while a halo-tags request is in flight. Options are requested in a single
  // batch, so this is a single flag rather than tracked per option — consumers use
  // it (together with `loaded`) to hide the whole top-3 comparison section until a
  // fresh, unified response (or failure) is available, rather than showing some
  // options with stale/mismatched tags while others are still being (re)ranked.
  // Always cleared in getHaloTags' finally block, success or failure, so the
  // section never gets stuck hidden.
  const isFetching = ref(false)
  // dealsStore.financingOptionsHash as of the most recent getHaloTags attempt
  // (success, failure, or the empty-options no-op), whichever completed last.
  // Lets a fresh mount (e.g. useRecalculateHaloTagsOnOptionsUpdate) tell whether
  // the currently-visible option set has ever actually been sent to Halo, instead
  // of relying solely on `loaded`, which is set for the borrower's session as a
  // whole rather than for a specific option set.
  const lastRequestedHash = ref('')
  // Ids of the options covered by the most recently SETTLED request (success or
  // failure) — i.e. the options we've actually asked Halo about, whether or not
  // each one got a tag back. Consumers (e.g. OfferComparisonContainer) filter
  // availableOptions down to this set so an option only appears on screen once
  // it's been through a completed request, without ever hiding options that were
  // already visible from a prior request while a new one is in flight.
  const resolvedOptionIds = ref(new Set())
  // Bumped at the start of every getHaloTags call. Lets a request's own try/finally
  // tell whether it's still the most recent one before touching shared state — if a
  // newer call started while an older one was still in flight, the older call's
  // result is stale and must not overwrite the newer call's state when it resolves.
  let requestSeq = 0

/*
  ██████  ███████ ████████ ████████ ███████ ██████  ███████
 ██       ██         ██       ██    ██      ██   ██ ██
 ██   ███ █████      ██       ██    █████   ██████  ███████
 ██    ██ ██         ██       ██    ██      ██   ██      ██
  ██████  ███████    ██       ██    ███████ ██   ████ ███████
 GETTERS
*/
  const haloTags = computed(() => Object.values(haloTagsByOptionId.value))

  // Look up the halo tag for a single option (by offer option id).
  const haloTagFor = computed(() => (optionId) => haloTagsByOptionId.value[optionId] ?? null)

  // Map an option's halo tag to its OPTION_INSIGHTS entry ({ tag, insight }).
  // Halo returns the tag as 'Speed' | 'Cost' | 'Amount' | 'Recommended' (or null);
  // uppercasing it yields the OPTION_INSIGHTS key (SPEED/COST/AMOUNT/RECOMMENDED).
  const optionInsightFor = computed(() => (optionId) => {
    const tagKey = haloTagsByOptionId.value[optionId]?.tag
    if (!tagKey) return null
    const key = String(tagKey).toUpperCase()
    const insight = OPTION_INSIGHTS[key]
    return insight ? { key, ...insight } : null
  })

/*
   █████   ██████ ████████ ██  ██████  ███    ██ ███████
  ██   ██ ██         ██    ██ ██    ██ ████   ██ ██
  ███████ ██         ██    ██ ██    ██ ██ ██  ██ ███████
  ██   ██ ██         ██    ██ ██    ██ ██  ██ ██      ██
  ██   ██  ██████    ██    ██  ██████  ██   ████ ███████
  ACTIONS
  ! - - Actions calling other actions in the same store must use `this.actionName(...)`
  ! - - If we do not use `this.actionName` it will not be properly mockable in tests.
*/

  // Extract the financial values the modeling service needs from an option.
  // Offers/options (isStaticOffer) carry these values flat on the item; approvals
  // keep them nested and must be derived from their default (highest-amount)
  // change via the approvals store, the same way the option cards do.
  //
  // `paymentAmount` (flat on offers/options, or from buildOutput for approvals) is a
  // SINGLE payment per the option's payment frequency (daily/weekly/bi-monthly/...),
  // not a monthly figure. The modeling service expects a monthly payment, so normalize
  // it via the frequency before sending. If it can't be converted (unknown frequency /
  // missing amount), fall back to the raw per-period amount rather than emitting null.
  function extractOptionFinancials(option) {
    if (option.isStaticOffer) {
      const calculatedData = !option.isOfferOption ? OfferTotal.getOfferTotal(option) : null
      const amount = calculatedData ? Number(calculatedData.offerAmount) : Number(option.amount)
      // Derive payback explicitly rather than wrapping in Number(): factorRate can be
      // undefined (e.g. LOC/term offer options carry no factor).
      // Leave payback null when there's nothing valid to compute it from.
      // getOfferTotal already does everything possible to calculate paybackAmount from the
      // option's own fields, so these else-if fallbacks are only reachable for isOfferOption items
      let payback = null
      if (calculatedData) {
        payback = calculatedData.paybackAmount
      } else if (option.payback != null) {
        payback = Number(option.payback)
      } else if (option.factorRate != null) {
        payback = Number(option.factorRate) * amount
      } else if (option.paymentAmount != null && option.numPayments != null) {
        // No payback or factor: the total repaid is the per-period payment across
        // every payment in the loan's life.
        payback = Number(option.paymentAmount) * Number(option.numPayments)
      }
      const monthlyPayment = getMonthlyPaymentFromPeriod(calculatedData?.paymentAmountByPeriod ?? option.paymentAmount, option.paymentFrequency, option.numPayments)
      return {
        amount,
        monthlyPayment: Number(monthlyPayment ?? option.paymentAmount ?? 0), // fallback to raw per-period amount if frequency unknown, then to 0
        // term can be stated in months/weeks/days (offer options); the service wants months.
        term: convertTermToMonths(option.term, option.termType),
        payback,
        factorRate: option.factorRate,
      }
    }

    const output = approvalsStore.buildOutput(option, approvalsStore.initialChange(option))
    const maxAmount = getApprovalMaxAmount(option, getApprovalOption(option, approvalsStore.initialChange))
    const amount = Number(output.amount ?? maxAmount)
    // buildOutput strips `payback` for LOC (skipPropsByType) but exposes estimatedPayback;
    // fall back to it, leaving payback null only when neither is present.
    const rawPayback = output.payback ?? output.estimatedPayback
    const payback = rawPayback != null ? Number(rawPayback) : null
    const monthlyPayment = getMonthlyPaymentFromPeriod(output.paymentAmount, output.paymentFrequency, output.numOfPayments ?? output.payments)
    return {
      amount,
      monthlyPayment: Number(monthlyPayment ?? output.paymentAmount ?? 0),  // fallback to raw per-period amount if frequency unknown, then to 0
      term: convertTermToMonths(output.term, output.termType),
      payback,
      factorRate: output.factorRate,
    }
  }

  // Build the per-option payload from an option (see dealsStore.availableOptions —
  // an offer, option, or approval). The modeling service requires all fields.
  function buildOptionPayload(option) {
    const { amount, monthlyPayment, term, payback, factorRate } = extractOptionFinancials(option)
    // min_calc_cost is the option's cost expressed as a factor: use the option's own
    // factorRate when it's defined, otherwise derive an equivalent rate as payback / amount.
    // `payback` can be null when it couldn't be derived at all, and `amount` can be null
    const minCalcCost = factorRate != null && Number.isFinite(Number(factorRate)) && factorRate > 0
      ? Number(factorRate)
      : (amount > 0 && payback != null ? Math.round(payback / amount * 1000) / 1000 : null)
    return {
      option_id: option.financingOptionId,
      lender_name: option.loanProductLender ?? option.lenderName,
      loan_product_id: option.loanProductId,
      product_name: option.loanProductCategoryName,
      max_amount: amount,
      max_monthly_payment: monthlyPayment,
      max_term: term,
      min_calc_cost: minCalcCost,
    }
  }

  function buildRequestBody(options) {
    const borrowerValues = borrowerStore.borrowerValues
    return {
      average_monthly_sales: Number(get(borrowerValues, 'average_monthly_sales.value')),
      credit_score: Number(get(borrowerValues, 'creditScore.value')),
      entity_type: get(borrowerValues, 'entity_type.value'),
      lendio_industry: get(borrowerValues, 'lendioIndustry.value'),
      number_of_employees: Number(get(borrowerValues, 'number_of_employees.value')),
      requested_amount: Number(get(borrowerValues, 'financeAmount.value')),
      time_in_business: borrowerStore.getMonthsInBusiness,
      // The modeling service requires every field to be a real number — drop any option
      // where derivation came up short rather than sending a partially-null payload.
      options: options
        .map(buildOptionPayload)
        .filter((o) => (
          Number.isFinite(o.min_calc_cost)
          && Number.isFinite(o.max_term)
          && Number.isFinite(o.max_monthly_payment)
          && o.min_calc_cost >= 1
        )),
    }
  }

  async function getHaloTags(force = false) {
    // Halo tags (the new top-card insight tags) are only shown to borrowers in
    // the NEW_OPTION_TOP_CARD experiment (id 140). Skip the request otherwise.
    if (!borrowerStore.inNewOptionTopCardExperiment) return

    const borrowerId = borrowerStore.borrowerId
    if (!borrowerId) return

    if (loaded.value && !force) return

    const requestedHash = dealsStore.financingOptionsHash

    // Request halo tags for every available option, not just the top 3 shown on
    // the /portal/loptions comparison page. We only display tags on the top cards,
    // but the modeling service needs the full option set to rank them correctly.
    const options = dealsStore.availableOptions
    if (!options.length) {
      loaded.value = true
      lastRequestedHash.value = requestedHash
      resolvedOptionIds.value = new Set()
      return
    }

    const thisRequest = ++requestSeq
    isFetching.value = true
    // Record the hash as soon as we commit to requesting it — not after the
    // round-trip completes — so a second caller checking "has this hash already
    // been requested" during a slow/throttled request sees it immediately,
    // instead of firing a redundant duplicate request for the same hash.
    lastRequestedHash.value = requestedHash

    try {
      const requestBody = buildRequestBody(options)
      if (!requestBody.options?.length) {
        haloTagsByOptionId.value = {}
        return
      }
      const { data, status } = await $axios.post(
        `${env('apiUrl')}/borrower-portal/borrower/${borrowerId}/halo-tags`,
        requestBody,
        { timeout: HALO_TAGS_TIMEOUT_MS }
      )

      // A newer getHaloTags call started while we were waiting — that call owns
      // isFetching/lastRequestedHash now, and its response is the one that should
      // win, so bail out here rather than applying our now-stale result over it.
      if (thisRequest !== requestSeq) return

      const responseOptions = get(data, 'data.options', [])
      if (status >= 200 && status < 300 && Array.isArray(responseOptions)) {
        haloTagsByOptionId.value = responseOptions.reduce((acc, tag) => {
          acc[tag.option_id] = tag
          return acc
        }, {})
      }
    } catch (e) {
      if (thisRequest !== requestSeq) return
      // Clear any tags from a prior successful request — otherwise a failed
      // recalculation would reveal the section again (isFetching clears in
      // finally either way) still showing stale tags for the old option set.
      haloTagsByOptionId.value = {}
      log.error('Unable to get halo tags for borrower', {
        err: e,
        errMessage: e.message,
        borrowerId,
      })
    } finally {
      if (thisRequest === requestSeq) {
        loaded.value = true
        // Clear regardless of outcome — a failed request should still reveal
        // options (without tags), never leave the section stuck hidden.
        isFetching.value = false
        // Mark this batch resolved whether it succeeded or failed — a failed
        // request still reveals its options (just without tags), rather than
        // leaving them permanently excluded from visibleOptions.
        resolvedOptionIds.value = new Set(options.map((option) => option.financingOptionId))
      }
    }
  }

  function reset() {
    // Invalidate any in-flight request so its finally can't reapply state after reset.
    requestSeq += 1
    haloTagsByOptionId.value = {}
    isFetching.value = false
    loaded.value = false
    lastRequestedHash.value = ''
    resolvedOptionIds.value = new Set()
  }

  return {
    //STATE
    haloTagsByOptionId,
    loaded,
    isFetching,
    lastRequestedHash,
    resolvedOptionIds,
    //GETTERS
    haloTags,
    haloTagFor,
    optionInsightFor,
    //ACTIONS
    getHaloTags,
    reset,
  }
})
